import { useState, useEffect, useRef } from "react";
import { API_URL } from "../../config/api";
import ToastMessage from "../../config/ToastMessage";

const USER_TYPES = ["Organization Admin", "Training Provider Admin"];
const ACCENT = "#4AACC5";

function validateForm(form) {
  const errors = {};
  if (!form.type) errors.type = "User type is required.";
  if (!form.name.trim()) {
    errors.name = "User name is required.";
  } else if (form.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters.";
  }
  if (!form.email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "Enter a valid email address.";
  }
  return errors;
}

export default function AddUserModal({ isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState({ type: "", name: "", email: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null); // { type: "success"|"error", message: string }
  const firstInputRef = useRef(null);

  // Focus first field when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) handleCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Live validation after first touch
  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      setErrors(validateForm(form));
    }
  }, [form, touched]);

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const resetState = () => {
    setForm({ type: "", name: "", email: "" });
    setErrors({});
    setTouched({});
    setLoading(false);
    setSubmitted(false);
    setToast(null);
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    setTouched({ type: true, name: true, email: true });
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    setToast(null);
    try {
      const token = localStorage.getItem("access_token"); 

      const response = await fetch(`${API_URL}/user/invite_user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          type: form.type,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        // Extract a meaningful error message from the backend response
        const message =
          data?.detail ||
          data?.message ||
          (typeof data === "string" ? data : null) ||
          `Request failed (${response.status})`;

        // Show error inline and as toast
        setErrors({ submit: message });
        setToast({ type: "error", message });
        setLoading(false);
        return;
      }

      // Success
      const successMessage =
        data?.message || data?.detail || "Invitation sent successfully!";

      setSubmitted(true);
      setToast({ type: "success", message: successMessage });

      setTimeout(() => {
        onSuccess?.({ ...form, ...data });
        resetState();
        onClose();
      }, 1500);
    } catch (err) {
      const message =
        err?.message === "Failed to fetch"
          ? "Network error. Please check your connection."
          : err?.message || "Something went wrong. Please try again.";

      setErrors({ submit: message });
      setToast({ type: "error", message });
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-zinc-700 placeholder-zinc-300 focus:outline-none focus:ring-2 transition-colors ${
      touched[field] && errors[field]
        ? "border-red-400 focus:ring-red-100"
        : "border-zinc-200 focus:ring-sky-100"
    } disabled:bg-zinc-50 disabled:cursor-not-allowed`;

  const ErrorMsg = ({ id, msg }) =>
    msg ? (
      <p id={id} role="alert" className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
        <svg className="w-3 h-3 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {msg}
      </p>
    ) : null;

  return (
    <>
      {/* Toast notification — rendered outside modal so it's always visible */}
      {toast && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={handleCancel}
      />

      {/* Modal container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-10"
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative flex flex-col"
          style={{ maxHeight: "calc(100dvh - 48px)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-zinc-100 shrink-0">
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: "#EAF6FA" }}
              >
                <svg className="w-4 h-4" fill="none" stroke={ACCENT} strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
              </div>
              <h2 id="modal-title" className="text-base font-semibold text-zinc-800">
                Invite User
              </h2>
            </div>
            <button
              onClick={handleCancel}
              disabled={loading}
              aria-label="Close modal"
              className="text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg p-1.5 transition-colors disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable body */}
          <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 space-y-4">

            {/* API error banner */}
            {errors.submit && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2.5">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {errors.submit}
              </div>
            )}

            {/* Success banner */}
            {submitted && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs rounded-lg px-3 py-2.5">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Invitation sent successfully!
              </div>
            )}

            {/* User Type */}
            <div>
              <label htmlFor="user-type" className="flex items-center gap-0.5 text-sm font-medium text-zinc-700 mb-1.5">
                User Type <span className="text-red-500 ml-0.5">*</span>
              </label>
              <div className="relative">
                <select
                  id="user-type"
                  ref={firstInputRef}
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  onBlur={() => handleBlur("type")}
                  disabled={loading || submitted}
                  aria-invalid={!!(touched.type && errors.type)}
                  aria-describedby={touched.type && errors.type ? "type-error" : undefined}
                  className={`${inputClass("type")} appearance-none pr-9 cursor-pointer`}
                >
                  <option value="">Select User Type</option>
                  {USER_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              <ErrorMsg id="type-error" msg={touched.type && errors.type} />
            </div>

            {/* User Name */}
            <div>
              <label htmlFor="user-name" className="flex items-center gap-0.5 text-sm font-medium text-zinc-700 mb-1.5">
                User Name <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                id="user-name"
                type="text"
                placeholder="Enter Name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                disabled={loading || submitted}
                aria-invalid={!!(touched.name && errors.name)}
                aria-describedby={touched.name && errors.name ? "name-error" : undefined}
                autoComplete="name"
                className={inputClass("name")}
              />
              <ErrorMsg id="name-error" msg={touched.name && errors.name} />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="user-email" className="flex items-center gap-0.5 text-sm font-medium text-zinc-700 mb-1.5">
                Email <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                id="user-email"
                type="email"
                placeholder="Enter Email Address"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                disabled={loading || submitted}
                aria-invalid={!!(touched.email && errors.email)}
                aria-describedby={touched.email && errors.email ? "email-error" : undefined}
                autoComplete="email"
                className={inputClass("email")}
              />
              <ErrorMsg id="email-error" msg={touched.email && errors.email} />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-4 border-t border-zinc-100 shrink-0">
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50 active:bg-zinc-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || submitted}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: ACCENT }}
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Sending…
                  </>
                ) : submitted ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Sent!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4 20-7z" />
                    </svg>
                    Send Invitation Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}